import {
  addToaster,
  discardFaxById,
  fetchPrintDocument,
  hideLoader,
  markCompleteStatusPrintDoc,
  showLoader,
  updateStatusPrintMailAPI,
} from '@app-store'
import {
  ConfirmDialogWithReason,
  CustomButton,
  Loader,
  PdfViewer,
  SvgDownloadGrey,
  SvgVerticalSplitter,
  Tooltip,
} from '@ui-kit'
import React, { useEffect, useState } from 'react'
import { QUEUE_STRINGS } from '../../utils/queueIntake.types'
import { COMMUNICATION_STATUSES, NAMESPACES } from '../../utils/constants'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { v4 as uuid } from 'uuid'
import { FaxDetails } from '../../types/intakeQueue.types'
import printJS from 'print-js'
import { PRINT_MAIL_STRING } from '../../utils/strings'
import { isReadOnlyUser } from '../../utils/permission'

const LetterPreview: React.FC<FaxDetails> = ({
  faxDetails,
  communicationStatuses,
}) => {
  const dispatch = useAppDispatch()
  const userId = useAppSelector((state: RootState) => state?.authUser?.user?.id)
  const [faxUrl, setFaxUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [markComplete, setMarkComplete] = useState<boolean>(false)

  const getFaxUrl = async () => {
    try {
      setIsLoading(true)

      const response = await fetchPrintDocument(
        faxDetails?.id?.toString() ?? ''
      )
      const updatedUrl = response.data.replace(/\s/g, '')
      setFaxUrl(updatedUrl)
    } catch (error) {
      console.error(error)
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const downloadFile = () => {
    fetch(faxUrl)
      .then((response) => {
        const fileName = `${faxDetails?.communication_id ?? 'intakePdf'}.pdf`

        return response.blob().then((blob) => ({ blob, fileName }))
      })
      .then(({ blob, fileName }) => {
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.download = fileName ?? 'intakePdf'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      })
      .catch((error) => {
        console.error('Error fetching the file:', error)
      })
  }

  const discardFax = () => {
    setIsModalOpen(true)
  }

  const handlePrint = async () => {
    try {
      dispatch(showLoader())
      const bulkDoc = await fetchPrintDocument(faxDetails?.id?.toString() ?? '')
      const statusResponse = await updateStatusPrintMailAPI(
        (faxDetails?.id ?? 0)?.toString(),
        {
          meta_data: {
            entity_id: (faxDetails?.communication_key ?? -1)?.toString(),
            embedded: ['category_status_id'],
          },
          data: {
            category_status_id:
              communicationStatuses?.find(
                (res) =>
                  res.label === COMMUNICATION_STATUSES?.PROCESSING_PRINTING
              )?.id ?? '-1',
          },
        }
      )
      if (statusResponse) {
        dispatch(
          addToaster({
            id: uuid(),
            message: PRINT_MAIL_STRING.SINGLE_PROCESSING_PRINTING,
            type: 'success',
          })
        )
      }
      const res = await fetch(bulkDoc.data)
      if (res.url) {
        dispatch(hideLoader())
        printJS({
          printable: res.url.replace(/\s+/g, ''),
          type: 'pdf',
        })
      }
    } catch (error) {
      dispatch(hideLoader())
      console.error(error)
    }
  }

  const markComplte = async () => {
    const success = await markCompleteStatusPrintDoc(
      faxDetails?.id.toString() ?? ''
    )
    if (success) {
      setMarkComplete(true)
      dispatch(
        addToaster({
          id: uuid(),
          message: PRINT_MAIL_STRING.SINGLE_COMPLETED,
          type: 'success',
        })
      )
    }
  }

  const onSubmit = async (reason: string) => {
    try {
      dispatch(showLoader())
      await discardFaxById(
        {
          id: faxDetails?.id?.toString() ?? '',
          reason: reason,
          reviewer: userId ?? 0,
        },
        faxDetails?.programs?.id?.toString() ??
          faxDetails?.hcp_program?.id?.toString()
      )
      // unlockOnDiscard.current = false
      dispatch(
        addToaster({
          message: PRINT_MAIL_STRING.SINGLE_DISCARD,
          type: 'success',
          id: uuid(),
        })
      )
      // navigate('../')
      setIsModalOpen(false)
    } catch (err) {
      console.error(err)
      // unlockOnDiscard.current = true
      dispatch(
        addToaster({
          message: QUEUE_STRINGS.ERROR_DISCARDING_FAX,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    getFaxUrl()
    if (faxDetails?.category_status?.label === QUEUE_STRINGS.COMPLETED) {
      setMarkComplete(true)
    }
  }, [faxDetails])

  return isLoading ? (
    <Loader isGlobal={true} loaderHeight="126" loaderWidth="126" />
  ) : (
    <div className="h-full flex flex-col">
      {isModalOpen && (
        <ConfirmDialogWithReason
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onConfirm={onSubmit}
          message={NAMESPACES.DISCARD_LETTER_CONFIRMATION_MESSAGE}
          width="w-[38.5rem]"
          validationMessage={
            NAMESPACES.DISCARD_REASON_VALIDATION_MESSAGE_LETTER
          }
          showCloseButton={true}
        />
      )}
      <div className="flex justify-between items-center">
        <h3 className="font-primary font-medium text-base">
          {faxDetails?.communication_id}
          {!isReadOnlyUser() ? (
            <span
              onClick={markComplte}
              className={`${
                !markComplete
                  ? 'bg-accent-0 border border-primary-500 text-primary-500 cursor-pointer '
                  : 'bg-tertiary-200 text-accent-600 cursor-not-allowed pointer-events-none p'
              }text-xs px-3 py-1 ml-4 inline-block rounded-[0.25rem]`}
            >
              {QUEUE_STRINGS.MARK_AS_COMPLETE}
            </span>
          ) : null}
        </h3>
        {!isReadOnlyUser() ? (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className={`flex items-center font-primary text-primary-900 justify-center font-light text-xs w-[4.313rem] h-[1.75rem] rounded border border-primary-500 hover:bg-primary-100`}
              onClick={() => discardFax()}
              data-qa-id="discard-intake"
            >
              {QUEUE_STRINGS.DISCARD}
            </button>

            <SvgVerticalSplitter className={`ml-3 mr-3`} />
            <button
              type="button"
              className={`flex items-center font-primary text-primary-900 justify-center font-light text-xs w-[4.313rem] h-[1.75rem] rounded border border-primary-500 hover:bg-primary-100`}
              onClick={() => {
                handlePrint()
              }}
              data-qa-id="discard-intake"
            >
              {QUEUE_STRINGS.PRINT}
            </button>
            <Tooltip content={QUEUE_STRINGS.DOWNLOAD}>
              <CustomButton
                type="button"
                color="others"
                widthClass="w-10"
                horizontalPaddingClasses="px-0"
                className="h-[1.75rem]"
                isFilled={true}
                onClick={() => downloadFile()}
                data-qa-id="download-file"
              >
                <SvgDownloadGrey />
              </CustomButton>
            </Tooltip>
          </div>
        ) : null}
      </div>

      <div className="flex-1 view-fax-wrap w-[100%] letter-view-box bg-accent-50 border rounded-[.5rem] border-accent-200 p-12 mt-1 overflow-y-auto">
        {/* Placeholder for Fax Preview */}
        <div className="max-w-[46.87rem] my-0 mx-auto relative h-full">
          <PdfViewer pdfUrl={faxUrl} />
        </div>
      </div>
    </div>
  )
}

export default LetterPreview
